import React from 'react';
import { useTranslation } from '@wix/yoshi-flow-editor';
import { Divider, Text, TextTypography } from 'wix-ui-tpa/cssVars';

import {
  useOrderTrackingPageStorage,
  withOrderTrackingPageStorageProvider,
} from '../storage';
import { orderTrackingPageDataHooks as dataHooks } from '../constants';
import { withLayoutSizeProvider } from '../../../components-shared/LayoutSizeProvider';
import { RtlProvider } from '../../../components-shared/RtlProvider';
import { getDueBy } from '../../../utils/timeFormatter';
import { RequestStatus } from '../../../utils/wrapRequest';
import { LAYOUT_BREAKPOINTS_START } from '../../../utils/constants';

import {
  CONTENT_TEXT,
  statusDescriptionTranslationKeysMap,
  statusTitleTranslationKeysMap,
} from './Text';
import { classes, st } from './Widget.st.css';

const Widget = () => {
  const { t } = useTranslation();
  const { orderNormalized, orderStatus, orderRequestStatus } =
    useOrderTrackingPageStorage();

  const isPickup = orderNormalized?.isPickupOrder;

  const address = isPickup
    ? orderNormalized?.formattedPickupAddress
    : orderNormalized?.formattedDeliveryAddress;

  const sectionTitle = isPickup
    ? t(CONTENT_TEXT.pickupDetailsTitle)
    : t(CONTENT_TEXT.deliveryDetailsTitle);
  const locationLabel = isPickup
    ? t(CONTENT_TEXT.pickupAddressLabel)
    : t(CONTENT_TEXT.deliveryAddressLabel);
  const timeLabel = isPickup
    ? t(CONTENT_TEXT.pickupTimeLabel)
    : t(CONTENT_TEXT.deliveryTimeLabel);

  const deliveryTime =
    orderNormalized?.fulfillmentDateTimeFrom ||
    orderNormalized?.fulfillmentDateTimeTo
      ? getDueBy(
          orderNormalized.fulfillmentDateTimeFrom,
          orderNormalized.fulfillmentDateTimeTo,
        )
      : '';

  const statusTitle = orderStatus
    ? t(
        statusTitleTranslationKeysMap[orderStatus] ??
          statusTitleTranslationKeysMap.Unknown,
      )
    : '';

  const statusDescription = orderStatus
    ? t(
        statusDescriptionTranslationKeysMap[orderStatus] ??
          statusDescriptionTranslationKeysMap.Unknown,
      )
    : '';

  return (
    <RtlProvider>
      <div className={classes.root}>
        {orderRequestStatus === RequestStatus.FAILED && (
          <div
            className={st(
              classes.block,
              classes.statusSurface,
              classes.errorBlock,
            )}
            data-hook={dataHooks.error()}
          >
            <Text
              tagName="h2"
              typography={TextTypography.smallTitle}
              className={classes.statusHeading}
            >
              {t(CONTENT_TEXT.errorTitle)}
            </Text>
            <Text
              typography={TextTypography.runningText}
              className={classes.statusDescription}
            >
              {t(CONTENT_TEXT.errorDescription)}
            </Text>
          </div>
        )}
        {orderRequestStatus === RequestStatus.RESOLVED && orderNormalized && (
          <div className={classes.content}>
            <div className={classes.header}>
              <Text
                tagName="h1"
                typography={TextTypography.largeTitle}
                className={classes.title}
                data-hook={dataHooks.title()}
              >
                {t(CONTENT_TEXT.title)}
              </Text>
              <div className={classes.orderNumber}>
                <Text
                  typography={TextTypography.runningText}
                  className={classes.orderNumberText}
                >
                  {t(CONTENT_TEXT.orderNumberLabel)}
                </Text>
                <Text
                  typography={TextTypography.runningText}
                  className={classes.orderNumberText}
                  data-hook={dataHooks.orderNumber()}
                >
                  <bdi>{orderNormalized?.order?.number}</bdi>
                </Text>
              </div>
            </div>

            <div className={st(classes.block, classes.statusSurface)}>
              <div className={classes.statusBlock}>
                <Text
                  typography={TextTypography.runningText}
                  className={classes.statusLabel}
                  data-hook={dataHooks.statusLabel()}
                >
                  {t(CONTENT_TEXT.statusLabel)}
                </Text>
                <Text
                  tagName="h2"
                  typography={TextTypography.smallTitle}
                  className={classes.statusHeading}
                  data-hook={dataHooks.statusTitle()}
                >
                  {statusTitle}
                </Text>
                <Text
                  typography={TextTypography.runningText}
                  className={classes.statusDescription}
                  data-hook={dataHooks.statusDescription()}
                >
                  {statusDescription}
                </Text>
              </div>
            </div>

            {deliveryTime && (
              <div
                className={st(
                  classes.block,
                  classes.fulfillmentSurface,
                  classes.detailsBlock,
                )}
              >
                <div className={classes.detailsHeader}>
                  <Text
                    tagName="h3"
                    typography={TextTypography.smallTitle}
                    className={classes.detailsHeaderTitle}
                    data-hook={dataHooks.detailsTitle()}
                  >
                    {sectionTitle}
                  </Text>
                </div>
                <Divider className={classes.detailsDivider} />
                <div className={classes.detailsBody}>
                  {address && (
                    <div className={classes.detailsColumn}>
                      <Text
                        typography={TextTypography.runningText}
                        className={classes.detailLabel}
                      >
                        {locationLabel}
                      </Text>
                      <Text
                        typography={TextTypography.runningText}
                        className={classes.detailText}
                        data-hook={dataHooks.deliveryAddress()}
                      >
                        {address}
                      </Text>
                    </div>
                  )}
                  <div className={classes.detailsColumn}>
                    <Text
                      typography={TextTypography.runningText}
                      className={classes.detailLabel}
                    >
                      {timeLabel}
                    </Text>
                    <Text
                      typography={TextTypography.runningText}
                      className={classes.detailText}
                      data-hook={dataHooks.deliveryTime()}
                    >
                      {deliveryTime}
                    </Text>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </RtlProvider>
  );
};

export default withLayoutSizeProvider(
  withOrderTrackingPageStorageProvider(Widget),
  LAYOUT_BREAKPOINTS_START,
);
